import React from 'react';
import { Form, Button, Input, Checkbox, Select, Tag } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'src/AuthContext';
import { toast } from 'sonner';
import { capitalize } from 'src/utils/capitalize';

import { Fields } from '../fields';
import {
  PRODUCT_LINES,
  PROJECT_SCOPES,
  DEFAULT_STYLE_INFO_VALUES,
} from '../../constants/projectConstants';
import StyleDropdowns from '../Common/StyleDropdowns';

import type { UnitType, CreateType } from './types';
import type { ProductLine, StyleInfo } from '../../types/project';

export interface DesignFormValues extends StyleInfo {
  projectName?: string;
  versionName?: string;
  // unitName removed
  productLine: ProductLine;
  customerEmail?: string;
  salesforceId?: string;
  customerName?: string;
  projectScope?: string;
  customProjectScope?: string;
  isPostFieldMeasure?: boolean;
  unitNumbersToInclude?: Array<{ name: string; unitTypeId: string }>;
}

interface DesignFormProps {
  onSubmit: (values: DesignFormValues) => Promise<void>;
  loading: boolean;
  unitType: UnitType;
  createType: CreateType;
}

const DesignForm: React.FC<DesignFormProps> = ({ onSubmit, loading, createType }) => {
  const [form] = Form.useForm();
  const projectScope = Form.useWatch('projectScope', form);
  const postMeasureValue = Form.useWatch('isPostFieldMeasure', form);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [unitTypes, setUnitTypes] = React.useState<string[]>([]);
  const [selectedUnitTypeId, setSelectedUnitTypeId] = React.useState<string | null>(null);
  const [unitNumbersByType, setUnitNumbersByType] = React.useState<Record<string, string[]>>({});
  const passedEmail = searchParams.get('customerEmail') ?? undefined;
  const passedSfId = searchParams.get('sfId') ?? undefined;

  const showCustomerNameInput = !user.isRetailRep || !passedSfId;

  const customerNameField = {
    name: 'customerName',
    label: user.isMultifamily ? 'Developer' : 'Customer Name',
    placeholder: 'Williams',
    required: false,
  };

  const nameField =
    createType === 'project'
      ? {
          name: 'projectName',
          label: 'Project Name',
          placeholder: 'Enter project name',
          required: false,
        }
      : {
          name: 'versionName',
          label: 'Version Name',
          placeholder: 'Enter version name',
          required: true,
          rules: [{ pattern: /^[^/]*$/, message: 'Version name cannot contain "/"' }],
        };

  const productLineDropdown = {
    name: 'productLine',
    label: 'Product Line',
    options: PRODUCT_LINES,
    required: true,
    placeholder: 'Select product line',
  };

  // NEW: Project Scope dropdown (like BlueprintForm)
  const projectScopeDropdown = {
    name: 'projectScope',
    label: 'Project Scope',
    options: PROJECT_SCOPES,
    required: true,
    placeholder: 'Please select',
  };

  const showSalesforceIdInput = user.isRetailRep || user.isMultifamily;

  React.useEffect(() => {
    if (!postMeasureValue) {
      setUnitTypes([]);
      setSelectedUnitTypeId(null);
      setUnitNumbersByType({});
    }
  }, [postMeasureValue]);
  // NEW: transform nested post-measure structure into backend list
  const buildUnitNumbersToInclude = (): Array<{ name: string; unitTypeId: string }> => {
    const result: Array<{ name: string; unitTypeId: string }> = [];
    const seen = new Set<string>();

    Object.entries(unitNumbersByType).forEach(([typeName, unitNumbers]) => {
      const unitTypeName = (typeName ?? '').trim();
      if (!unitTypeName) return;

      (unitNumbers ?? []).forEach((n) => {
        const unitNumberName = String(n ?? '').trim();
        if (!unitNumberName) return;

        const key = `${unitTypeName.toLowerCase()}::${unitNumberName.toLowerCase()}`;
        if (seen.has(key)) return;
        seen.add(key);

        result.push({ name: unitNumberName, unitTypeId: unitTypeName });
      });
    });

    return result;
  };

  // NEW: intercept finish so we can send flattened list
  const handleFinish = async (values: any) => {
    if (user.isMultifamily && values.isPostFieldMeasure) {
      const unitNumbersToInclude = buildUnitNumbersToInclude();

      if (unitNumbersToInclude.length === 0) {
        toast.error('Please select or add at least one unit number.');
        return;
      }

      await onSubmit({
        ...values,
        isPostFieldMeasure: postMeasureValue,
        unitNumbersToInclude,
      });

      return;
    }

    await onSubmit(values as DesignFormValues);
  };

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleFinish}
      initialValues={{
        ...DEFAULT_STYLE_INFO_VALUES,
        productLine: 'vista',
        customerEmail: passedEmail,
        salesforceId: passedSfId,
        isPostFieldMeasure: false,
        ...(createType === 'project' && passedEmail ? { projectName: passedEmail } : {}),
      }}
    >
      <Fields.Text config={nameField} />
      <Fields.Dropdown config={productLineDropdown} />

      {/* Multifamily-only fields */}
      {user.isMultifamily && (
        <>
          {/* Project Scope (MF only) */}
          <Fields.Dropdown config={projectScopeDropdown} />
          {projectScope?.toLowerCase() === 'custom' && (
            <Fields.Text
              config={{
                name: 'customProjectScope',
                label: 'Custom Project Scope',
                required: true,
                placeholder: 'Enter custom project scope',
              }}
            />
          )}

          <div style={{ marginTop: 16 }}>
            <h4>Post Field Measure</h4>
            <Form.Item
              name='isPostFieldMeasure'
              valuePropName='checked'
              style={{ marginBottom: 8 }}
            >
              <Checkbox>Yes</Checkbox>
            </Form.Item>

            {postMeasureValue && (
              <div className='mt-4 p-3 border rounded'>
                <h5>Select Units to Include</h5>
                <p className='text-muted small'>
                  Choose a unit type, then type unit numbers to include in this project.
                </p>

                {/* Unit Type (create via tags) */}
                <Form.Item label='Unit Type'>
                  <Select
                    mode='tags'
                    placeholder='Type unit types and press Enter'
                    style={{ width: '100%' }}
                    value={unitTypes}
                    onChange={(vals) => {
                      const nextTypes = (vals as string[]).map((v) => v.trim()).filter(Boolean);

                      const seen = new Set<string>();
                      const deduped: string[] = [];
                      for (const t of nextTypes) {
                        const key = t.toLowerCase();
                        if (seen.has(key)) continue;
                        seen.add(key);
                        deduped.push(t);
                      }

                      setUnitTypes(deduped);

                      if (selectedUnitTypeId) {
                        const match = deduped.find(
                          (t) => t.toLowerCase() === selectedUnitTypeId.toLowerCase(),
                        );
                        setSelectedUnitTypeId(match ?? null);
                      }

                      // ensure unitNumbersByType keys exist, and remove deleted types
                      setUnitNumbersByType((prev) => {
                        const next: Record<string, string[]> = {};
                        deduped.forEach((t) => {
                          // find matching key from previous (case-insensitive)
                          const existingKey = Object.keys(prev).find(
                            (k) => k.toLowerCase() === t.toLowerCase(),
                          );
                          next[t] = existingKey ? (prev[existingKey] ?? []) : [];
                        });
                        return next;
                      });
                    }}
                  />
                </Form.Item>

                {/* Pick which unit type you're editing */}
                {unitTypes.length > 0 && (
                  <Form.Item label='Active Unit Type'>
                    <Select
                      placeholder='Select a unit type'
                      style={{ width: '100%' }}
                      value={selectedUnitTypeId ?? undefined}
                      onChange={(val) => setSelectedUnitTypeId(val)}
                      allowClear
                      options={unitTypes.map((t) => ({ label: t, value: t }))}
                    />
                  </Form.Item>
                )}

                {/* Unit Numbers for selected type */}
                {selectedUnitTypeId && (
                  <Form.Item label='Unit Numbers'>
                    <Select
                      mode='tags'
                      placeholder='Select or type unit numbers'
                      style={{ width: '100%' }}
                      value={unitNumbersByType[selectedUnitTypeId] ?? []}
                      onInputKeyDown={(e) => {
                        if (e.ctrlKey || e.metaKey) return;
                        const allowed = [
                          'Backspace',
                          'Delete',
                          'ArrowLeft',
                          'ArrowRight',
                          'Tab',
                          'Enter',
                          'Home',
                          'End',
                        ];
                        if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
                      }}
                      onChange={(vals) => {
                        const nextUnitsRaw = (vals as string[])
                          .map((v) => v.trim())
                          .filter(Boolean);

                        let hadInvalid = false;
                        const nextUnits: string[] = [];

                        for (const u of nextUnitsRaw) {
                          if (!/^\d+$/.test(u)) {
                            hadInvalid = true;
                            continue;
                          }
                          nextUnits.push(u);
                        }

                        const deduped = Array.from(new Set(nextUnits));

                        if (hadInvalid) toast.error('Unit numbers must be numeric only');
                        setUnitNumbersByType((prev) => ({
                          ...prev,
                          [selectedUnitTypeId]: deduped,
                        }));
                      }}
                    />
                  </Form.Item>
                )}

                {/* Selected Units */}
                {Object.values(unitNumbersByType).some((arr) => (arr?.length ?? 0) > 0) && (
                  <div className='mt-3'>
                    <h6>Selected Units</h6>

                    {(() => {
                      const combined = Object.entries(unitNumbersByType).flatMap(
                        ([typeName, units]) =>
                          (units ?? []).map((u) => ({
                            key: `${typeName}::${u}`,
                            typeName,
                            unitName: u,
                            display: `${u} - Type ${typeName.toUpperCase()}`,
                          })),
                      );

                      combined.sort((a, b) => Number(a.unitName) - Number(b.unitName));

                      return (
                        <div className='d-flex flex-wrap gap-2'>
                          {combined.map((item) => (
                            <Tag
                              key={item.key}
                              closable
                              onClose={() => {
                                setUnitNumbersByType((prev) => ({
                                  ...prev,
                                  [item.typeName]: (prev[item.typeName] ?? []).filter(
                                    (x) => x !== item.unitName,
                                  ),
                                }));
                              }}
                            >
                              {item.display}
                            </Tag>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* STYLE SECTION – MF ONLY */}
          <div style={{ marginTop: 16 }}>
            <h4>Style</h4>
            <StyleDropdowns />
          </div>
        </>
      )}

      {showCustomerNameInput && (
        <Fields.Text key={customerNameField.name} config={customerNameField} />
      )}

      <Form.Item name='customerEmail' label='Customer Email'>
        <Input type='email' placeholder='customer@gmail.com' disabled={Boolean(passedEmail)} />
      </Form.Item>

      {showSalesforceIdInput && (
        <Form.Item name='salesforceId' label='Salesforce ID'>
          <Input placeholder='Enter Salesforce ID' disabled={Boolean(passedSfId)} />
        </Form.Item>
      )}

      <Form.Item className='mb-0'>
        <Button type='primary' htmlType='submit' loading={loading} block>
          Create {capitalize(createType)}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default DesignForm;
